import type {
  PermissionSystemSection,
  ModuleItem,
  ResourceItem,
} from '@/pages/configuracion/roles/ModulesPermissionsTable';

/**
 * ===== Tipos BACKEND (según lo que describiste) =====
 * [
 *  {
 *    id, key, name,
 *    modules: [
 *      { name, resources: [ { name, permissions: [...] } ] }
 *    ]
 *  }
 * ]
 */

interface BackendPermission {
  id: string;
  key: string; // proveedores.administracion.documentos_proveedor.consultar
  name: string;
  description?: string;
  shortDescription?: string;
  longDescription?: string;
  moduleId?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface BackendResource {
  name: string; // documentos_proveedor
  permissions: BackendPermission[];
}

interface BackendModule {
  name: string; // administracion | fiscal | inicio
  resources: BackendResource[];
}

export interface BackendSystem {
  id: string;
  key: string; // proveedores | rh | etc
  name: string; // Proveedores | RH
  modules: BackendModule[];
}

const EXTRAORDINARY_USER_ONLY_PERMISSION_KEYS = new Set([
  "isAreaManager",
  "canAssignSalaries",
]);

/**
 * ===== Helpers =====
 */

// proveedores.administracion.documentos_proveedor.consultar -> consultar
const extractAction = (permissionKey: string): string =>
  permissionKey.split(".").pop() ?? "";

const prettify = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

/**
 * =====================================================
 * 1) Backend -> UI (TODO en false)
 * =====================================================
 */
export const mapPermissionsToSections = (
  systems: BackendSystem[],
): PermissionSystemSection[] => {
  return systems
    .map((sys) => {
      const modules: ModuleItem[] = (sys.modules || [])
        .map((mod) => {
          const resources: ResourceItem[] = (mod.resources || [])
            .map((res) => {
              const permissions: Record<string, boolean> = {};

              (res.permissions || []).forEach((perm) => {
                if (EXTRAORDINARY_USER_ONLY_PERMISSION_KEYS.has(perm.key)) return;
                const action = extractAction(perm.key);
                permissions[action] = false;
              });

              return {
                key: `${sys.key}.${mod.name}.${res.name}`,
                name: prettify(res.name),
                realName: res.name,
                permissions,
              };
            })
            .filter((resource) => Object.keys(resource.permissions).length > 0);

          return {
            key: `${sys.key}.${mod.name}`,
            name: mod.name,
            resources,
          };
        })
        .filter((module) => module.resources.length > 0);

      return {
        id: sys.id,
        key: sys.key,
        title: sys.name,
        modules,
      };
    })
    .filter((system) => system.modules.length > 0);
};

/**
 * =====================================================
 * 2) Marcar activos al editar/consultar
 * rolePermissions: [{ key: 'proveedores....consultar' }, ...]
 * =====================================================
 */
export const applyRolePermissionsToSections = (
  systems: PermissionSystemSection[],
  rolePermissions: { key: string }[],
): PermissionSystemSection[] => {
  const activeKeys = new Set(rolePermissions.map((p) => p.key));

  return systems.map((sys) => ({
    ...sys,
    modules: sys.modules.map((mod) => ({
      ...mod,
      resources: mod.resources.map((res) => {
        const updatedPermissions: Record<string, boolean> = {};

        Object.keys(res.permissions).forEach((action) => {
          const fullKey = `${res.key}.${action}`; // res.key ya trae sys.mod.resource
          updatedPermissions[action] = activeKeys.has(fullKey);
        });

        return {
          ...res,
          permissions: updatedPermissions,
        };
      }),
    })),
  }));
};

/**
 * =====================================================
 * 3) Validación: Crear/Editar sin ninguna vista
 * =====================================================
 * El auto-marcado de la tabla cubre lo que el admin toca en la sesión, pero un
 * rol viejo puede venir ya guardado con Crear y sin Ver. Sin esto se vuelve a
 * guardar igual de roto: el usuario no puede entrar al módulo.
 */
const VIEW_ACTIONS = ["ver", "ver_asignadas", "read", "consultar", "consulta"];
const WRITE_ACTIONS = ["crear", "create", "editar", "update", "edit"];

export const findResourcesSinVista = (
  systems: PermissionSystemSection[],
): string[] => {
  const invalidos: string[] = [];

  systems.forEach((sys) =>
    sys.modules.forEach((mod) =>
      mod.resources.forEach((res) => {
        const acciones = Object.entries(res.permissions);
        const vistas = acciones.filter(([action]) =>
          VIEW_ACTIONS.includes(action.toLowerCase()),
        );
        // Recursos sin permiso de vista propio (Movimientos, Mantenimiento)
        // no tienen nada que exigir.
        if (!vistas.length) return;

        const escribe = acciones.some(
          ([action, enabled]) =>
            enabled && WRITE_ACTIONS.includes(action.toLowerCase()),
        );
        if (escribe && !vistas.some(([, enabled]) => enabled)) {
          invalidos.push(`${sys.title} › ${res.name}`);
        }
      }),
    ),
  );

  return invalidos;
};

/**
 * =====================================================
 * 4) UI -> Backend payload (solo keys activas)
 * =====================================================
 */
export const mapSectionsToPermissionKeys = (
  systems: PermissionSystemSection[],
): string[] => {
  const keys: string[] = [];

  systems.forEach((sys) => {
    sys.modules.forEach((mod) => {
      mod.resources.forEach((res) => {
        Object.entries(res.permissions).forEach(([action, enabled]) => {
          if (!enabled) return;
          keys.push(`${res.key}.${action}`);
        });
      });
    });
  });

  return keys;
};
